// frontend/src/components/PixClientsTab.js
//
// Aba "Pix" da Consulta de Clientes (ETAPAS 5a, 7a e 7b). Mostra DUAS listas:
//   1) os clientes cadastrados como Pix no Souli (GET /api/clients?payment_type=pix): busca no servidor, paginação, detalhes,
//      "$" (gerar cobrança) e apagar (só administrador; o servidor recusa se houver recorrência em aberto no Pix);
//   2) os clientes do PIX ANTIGO que ainda não estão cadastrados como Pix no Souli (GET /api/pix/clients, só leitura), com busca
//      por nome (sem acento), CPF (com ou sem pontos), e-mail ou telefone, e janela de detalhes. Quem já está na lista 1 não repete.
//   No modo `embedded` (busca da aba "Todos") a mesma tela aparece sob o título "Clientes Pix", só quando há um termo buscado.

import React, { useEffect, useRef, useState } from 'react';
import { DollarSign, Eye, Trash2, X, AlertTriangle } from 'lucide-react';
import { deletePixClient, pixGet } from '../services/pixApi';
import './PixClientsTab.css';

const PAGE_SIZE = 50;
const LEGACY_LIMIT = 200;
const STATUS_LABELS = { ACTIVE: 'Ativa', ATIVA: 'Ativa', PENDING: 'Pendente', PENDENTE: 'Pendente', CRIADA: 'Criada' };
const GRAU_LABELS = { filho: 'Filho(a)', conjuge: 'Cônjuge', pai: 'Pai/Mãe', agregado: 'Agregado' };

const onlyDigits = (value) => String(value == null ? '' : value).replace(/\D/g, '');
const brl = (n) => Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }).replace(/\u00a0/g, ' ');
const nameOf = (c) => String((c && (c.full_name || c.name)) || '—');
const plain = (value) => String(value == null ? '' : value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
const cityUf = (c) => (c.city ? `${c.city}${c.state ? `/${String(c.state).toUpperCase()}` : ''}` : '—');
const asText = (value) => (value == null || value === '' ? '—' : (typeof value === 'object' ? JSON.stringify(value) : String(value)));

function formatCpf(value) {
  const d = onlyDigits(value);
  return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : String(value || '—');
}

function formatPhone(value) {
  const d = onlyDigits(value);
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return value ? String(value) : '—';
}

function formatDate(value) {
  if (!value) return '—';
  const text = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text.split('-').reverse().join('/') : '—';
}

function formatAddress(c) {
  const street = [c.logradouro, c.numero].filter(Boolean).join(', ');
  const rest = [c.complemento, c.bairro].filter(Boolean).join(' - ');
  const place = [c.city, c.state ? String(c.state).toUpperCase() : ''].filter(Boolean).join('/');
  const zip = onlyDigits(c.zip_code);
  const parts = [street, rest, place, zip.length === 8 ? `CEP ${zip.slice(0, 5)}-${zip.slice(5)}` : ''].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

function describeDeleteError(error) {
  switch (error && error.code) {
    case 'PIX_ACCESS_DENIED': return 'Só administradores podem apagar clientes Pix.';
    case 'CLIENT_NOT_FOUND': return 'Este cliente não existe mais. Atualize a lista.';
    case 'CLIENT_HAS_LINKS': return error.message;
    case 'PIX_UNREACHABLE':
    case 'PIX_TIMEOUT': return 'Não consegui conferir as recorrências no Pix agora (o Pix não respondeu). Nada foi apagado; tente de novo em instantes.';
    case 'PIX_NOT_CONFIGURED': return 'A integração com o Pix ainda não está configurada no servidor. Avise o administrador.';
    default: return (error && error.message) || 'Não foi possível apagar. Tente novamente.';
  }
}

/** Cliente do Pix antigo → o mesmo formato do cadastro do Souli (para reaproveitar a janela de detalhes). */
function fromLegacy(c) {
  return {
    id: `antigo-${c.id}`, full_name: c.name, cpf: c.document, email: c.email, phone: c.phone, birth_date: c.birthDate,
    zip_code: c.zipCode, logradouro: c.street, numero: c.number, bairro: c.neighborhood, city: c.city, state: c.state,
    status: c.isActive === false ? 'inativo' : 'ativo', created_date: c.createdAt,
    _antigo: { enrollment: c.enrollment, patientName: c.patientName, recurrenceStatus: c.recurrenceStatus, chargeStatus: c.chargeStatus },
  };
}

/** O cliente do Pix antigo combina com o que foi digitado? Com letras: nome ou e-mail (sem acento). Só números: CPF ou telefone. */
function matchesLegacy(c, text) {
  const q = plain(text);
  if (!q) return true;
  if (/[a-z@]/.test(q)) return plain(c.name).includes(q) || plain(c.email).includes(q);
  const digits = onlyDigits(text);
  return digits.length >= 3 && (onlyDigits(c.document).includes(digits) || onlyDigits(c.phone).includes(digits));
}

/**
 * @param {Object} props
 * @param {Function} props.getClients getClients(queryString) da página (devolve { data, total, totalPages }).
 * @param {string} [props.query] texto da busca da página.
 * @param {boolean} [props.isAtendente] atendente só vê a lista depois de buscar (mesma regra das outras abas).
 * @param {string} [props.filterStatus] filtro de status da página.
 * @param {string} [props.filterCity] filtro de cidade da página.
 * @param {boolean} [props.canCharge] mostra o botão "$".
 * @param {string[]} [props.roles] papéis do usuário (o botão de apagar só aparece para admin).
 * @param {Function} [props.onGenerateCharge] chamada com o cliente ao clicar em "$".
 * @param {Function} [props.onTotal] recebe o total de clientes Pix (sem filtros): os do Souli mais os do Pix antigo que ainda não estão nele.
 * @param {boolean} [props.embedded] modo da busca da aba "Todos": só aparece (e só lê) quando há um termo de ao menos 2 caracteres.
 * @param {number} [props.debounceMs] pausa da digitação antes de buscar.
 */
export default function PixClientsTab({ getClients, query = '', isAtendente = false, filterStatus = '', filterCity = '', canCharge = false, roles = [], onGenerateCharge, onTotal, embedded = false, debounceMs = 350 }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [souliLoaded, setSouliLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [details, setDetails] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [blocking, setBlocking] = useState(null);
  const [legacy, setLegacy] = useState({ status: 'idle', list: [] }); // idle | loading | ok | error
  const [souliCpfs, setSouliCpfs] = useState(() => new Set());
  const [legacyReload, setLegacyReload] = useState(0);
  const requestRef = useRef(0);
  const deletingRef = useRef(false);
  const legacyAskedRef = useRef(-1);
  const mountedRef = useRef(true);

  const isAdmin = (roles || []).some((r) => String(r).toLowerCase() === 'admin');
  const text = query.trim();
  const needsSearch = isAtendente && text.length < 2;
  const idle = embedded && text.length < 2; // embutido na busca do "Todos": sem termo, nada a mostrar nem a ler

  useEffect(() => () => { mountedRef.current = false; }, []);
  useEffect(() => { setPage(1); }, [text, filterStatus, filterCity]);

  // 1) clientes cadastrados como Pix no Souli
  useEffect(() => {
    if (needsSearch || idle) { setRows([]); setTotal(0); setError(''); return undefined; }
    const timer = setTimeout(async () => {
      const mine = ++requestRef.current;
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({ sort: '-created_date', limit: String(PAGE_SIZE), page: String(page), payment_type: 'pix' });
        if (text) params.append('search', text);
        if (filterStatus) params.append('status', filterStatus);
        if (filterCity) params.append('cityName', filterCity);
        const answer = await getClients(params.toString());
        if (mine !== requestRef.current) return;
        setRows((answer.data || []).filter((c) => c && c.id && (!c.payment_type || c.payment_type === 'pix')));
        setTotal(answer.total || 0);
        setTotalPages(answer.totalPages || 1);
        setSouliLoaded(true);
      } catch (failure) {
        if (mine === requestRef.current) setError('Não foi possível carregar os clientes Pix. Tente novamente.');
      } finally {
        if (mine === requestRef.current) setLoading(false);
      }
    }, text ? debounceMs : 0);
    return () => { clearTimeout(timer); requestRef.current += 1; };
  }, [needsSearch, idle, text, page, filterStatus, filterCity, reload, getClients]); // eslint-disable-line react-hooks/exhaustive-deps

  // 2) clientes do Pix antigo: lidos UMA vez (e de novo só em "Tentar de novo"); a busca é feita aqui mesmo, na tela
  useEffect(() => {
    if (needsSearch || idle || legacyAskedRef.current === legacyReload) return;
    legacyAskedRef.current = legacyReload;
    setLegacy((previous) => ({ ...previous, status: 'loading' }));
    (async () => {
      try {
        const [old, mine] = await Promise.all([
          pixGet('/clients', { includeInactive: 'true' }),
          getClients('payment_type=pix&limit=500&page=1').catch(() => ({ data: [] })), // os CPFs que já estão no Souli (para não repetir)
        ]);
        if (!mountedRef.current) return;
        const list = (Array.isArray(old) ? old : (old && Array.isArray(old.data) ? old.data : [])).filter((c) => c && c.id && c.document);
        setSouliCpfs(new Set(((mine && mine.data) || []).map((c) => onlyDigits(c.cpf)).filter(Boolean)));
        setLegacy({ status: 'ok', list });
      } catch (failure) {
        if (mountedRef.current) setLegacy({ status: 'error', list: [] });
      }
    })();
  }, [needsSearch, idle, legacyReload]); // eslint-disable-line react-hooks/exhaustive-deps

  const legacyOnly = legacy.status === 'ok' ? legacy.list.filter((c) => !souliCpfs.has(onlyDigits(c.document))) : [];
  const legacyFound = legacyOnly
    .filter((c) => matchesLegacy(c, text))
    .filter((c) => !filterCity || plain(c.city) === plain(filterCity))
    .filter((c) => !['ativo', 'inativo'].includes(plain(filterStatus)) || (c.isActive === false) === (plain(filterStatus) === 'inativo'))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR'));
  const legacyMatches = legacyFound.slice(0, LEGACY_LIMIT);

  // a contagem da aba: os do Souli + os do Pix antigo que ainda não estão nele (só quando não há busca nem filtro)
  useEffect(() => {
    if (embedded || !onTotal || !souliLoaded || text || filterStatus || filterCity) return;
    onTotal(total + legacyOnly.length);
  }, [souliLoaded, total, legacy, souliCpfs, text, filterStatus, filterCity]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmDelete = async () => {
    if (deletingRef.current) return; // clique duplo: um só pedido
    deletingRef.current = true;
    setDeleting(true);
    setDeleteError('');
    try {
      await deletePixClient(toDelete.id);
      setToDelete(null);
      if (rows.length === 1 && page > 1) setPage(page - 1); else setReload((n) => n + 1);
    } catch (failure) {
      if (failure && failure.code === 'CLIENT_HAS_OPEN_RECURRENCE') setBlocking((failure.data && failure.data.existing) || []);
      else setDeleteError(describeDeleteError(failure));
    } finally {
      deletingRef.current = false;
      setDeleting(false);
    }
  };

  const closeDelete = () => { if (!deleting) { setToDelete(null); setBlocking(null); setDeleteError(''); } };

  if (idle) return null;
  if (needsSearch) {
    return (
      <div className="pixl-vazio">
        <h3>Realize uma busca para visualizar os clientes Pix</h3>
        <p>Digite o nome ou o CPF do cliente no campo de busca acima (ao menos 2 caracteres).</p>
      </div>
    );
  }

  const waitingLegacy = legacy.status === 'loading' || legacy.status === 'idle';
  const souliEmpty = !error && !loading && rows.length === 0;
  return (
    <div className={embedded ? 'pixl pixl-embutido' : 'pixl'}>
      {embedded && <h4 className="pixl-embutido-titulo">Clientes Pix</h4>}
      {error && <div className="pixl-erro" role="alert">{error} <button type="button" className="pixl-link" onClick={() => setReload((n) => n + 1)}>Tentar de novo</button></div>}
      {!error && loading && <p className="pixl-nota" role="status">Carregando clientes Pix...</p>}
      {souliEmpty && waitingLegacy && <p className="pixl-nota" role="status">Consultando o Pix antigo...</p>}
      {souliEmpty && !waitingLegacy && legacyMatches.length === 0 && embedded && <p className="pixl-nota">Nenhum cliente Pix encontrado para este termo.</p>}
      {souliEmpty && !waitingLegacy && legacyMatches.length === 0 && !embedded && (
        <div className="pixl-vazio">
          <h3>Nenhum cliente Pix encontrado</h3>
          <p>Para cadastrar, use Clientes → Cadastro de Clientes → Pix Santander.</p>
        </div>
      )}
      {souliEmpty && !waitingLegacy && legacyMatches.length > 0 && <p className="pixl-nota">Nenhum cliente cadastrado como Pix no Souli com esse termo. Veja abaixo os do Pix antigo.</p>}
      {rows.length > 0 && (
        <>
          <p className="pixl-nota">{total} {total === 1 ? 'cliente Pix' : 'clientes Pix'} no Souli</p>
          <div className="client-table-modern-wrapper">
            <table className="client-table-modern">
              <thead>
                <tr><th>CLIENTE</th><th>CPF</th><th>TELEFONE</th><th>PLANO</th><th>CIDADE</th><th>STATUS</th><th>AÇÕES</th></tr>
              </thead>
              <tbody>
                {rows.map((c, i) => (
                  <tr key={c.id} className={i % 2 === 0 ? 'row-even' : 'row-odd'}>
                    <td>
                      <div className="td-name-inner-modern">
                        <div className="mini-avatar-modern" style={{ background: '#0e7490' }}>{nameOf(c).charAt(0)}</div>
                        <div className="client-info-cell">
                          <div className="client-name-cell">{nameOf(c)}</div>
                          {c.email && <div className="client-email-cell">{c.email}</div>}
                        </div>
                      </div>
                    </td>
                    <td>{formatCpf(c.cpf)}</td>
                    <td>{formatPhone(c.phone)}</td>
                    <td>{c.plan_name || '—'}{c.plan_value ? <span className="pixl-sub"> {brl(c.plan_value)}</span> : null}</td>
                    <td>{cityUf(c)}</td>
                    <td><span className="pixl-status">{c.status || 'ativo'}</span></td>
                    <td>
                      <div className="pixl-acoes">
                        {canCharge && <button type="button" className="pixl-acao pixl-acao-cobrar" onClick={() => onGenerateCharge && onGenerateCharge(c)} title="Gerar cobrança" aria-label={`Gerar cobrança para ${nameOf(c)}`}><DollarSign size={15} /></button>}
                        <button type="button" className="pixl-acao" onClick={() => setDetails(c)} title="Ver detalhes" aria-label={`Ver detalhes de ${nameOf(c)}`}><Eye size={15} /></button>
                        {isAdmin && <button type="button" className="pixl-acao pixl-apagar" onClick={() => { setToDelete(c); setBlocking(null); setDeleteError(''); }} title="Apagar cliente" aria-label={`Apagar ${nameOf(c)}`}><Trash2 size={15} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="pixl-paginas">
              <button type="button" className="pixl-btn" onClick={() => setPage(page - 1)} disabled={page <= 1 || loading}>Anterior</button>
              <span>Página {page} de {totalPages}</span>
              <button type="button" className="pixl-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages || loading}>Próxima</button>
            </div>
          )}
        </>
      )}

      {legacy.status === 'error' && (
        <div className="pixl-erro" role="alert">Não consegui consultar o Pix antigo agora. <button type="button" className="pixl-link" onClick={() => setLegacyReload((n) => n + 1)}>Tentar de novo</button></div>
      )}
      {legacyMatches.length > 0 && (
        <section className="pixl-antigo" aria-label="Clientes do Pix antigo">
          <h4 className="pixl-antigo-titulo">Clientes do Pix antigo ({legacyFound.length})</h4>
          <p className="pixl-nota">Estão no sistema Pix antigo e ainda não foram cadastrados como Pix no Souli. Para ver as recorrências deles, use Cobranças → Pix.{legacyFound.length > LEGACY_LIMIT ? ` Mostrando os primeiros ${LEGACY_LIMIT}: refine a busca para ver os demais.` : ''}</p>
          <div className="client-table-modern-wrapper">
            <table className="client-table-modern">
              <thead>
                <tr><th>CLIENTE</th><th>CPF</th><th>TELEFONE</th><th>CIDADE</th><th>SITUAÇÃO NO PIX</th><th>AÇÕES</th></tr>
              </thead>
              <tbody>
                {legacyMatches.map((c, i) => (
                  <tr key={c.id} className={i % 2 === 0 ? 'row-even' : 'row-odd'}>
                    <td>
                      <div className="td-name-inner-modern">
                        <div className="mini-avatar-modern" style={{ background: '#64748b' }}>{nameOf({ name: c.name }).charAt(0)}</div>
                        <div className="client-info-cell">
                          <div className="client-name-cell">{nameOf({ name: c.name })}</div>
                          {c.email && <div className="client-email-cell">{c.email}</div>}
                        </div>
                      </div>
                    </td>
                    <td>{formatCpf(c.document)}</td>
                    <td>{formatPhone(c.phone)}</td>
                    <td>{cityUf(c)}</td>
                    <td><span className="pixl-status pixl-status-antigo">{c.isActive === false ? 'Inativo' : 'Ativo'}</span></td>
                    <td>
                      <div className="pixl-acoes">
                        <button type="button" className="pixl-acao" onClick={() => setDetails(fromLegacy(c))} title="Ver detalhes" aria-label={`Ver detalhes de ${nameOf({ name: c.name })} (Pix antigo)`}><Eye size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {details && (
        <div className="pixl-fundo" onClick={() => setDetails(null)}>
          <div className="pixl-modal" role="dialog" aria-modal="true" aria-labelledby="pixl-detalhes-titulo" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Escape') setDetails(null); }}>
            <div className="pixl-modal-topo">
              <h3 id="pixl-detalhes-titulo">{nameOf(details)}</h3>
              <button type="button" className="pixl-fechar" onClick={() => setDetails(null)} aria-label="Fechar"><X size={18} /></button>
            </div>
            {details._antigo && <p className="pixl-nota">Cliente do Pix antigo (ainda não cadastrado como Pix no Souli).</p>}
            <dl className="pixl-dl">
              <div><dt>CPF</dt><dd>{formatCpf(details.cpf)}</dd></div>
              <div><dt>E-mail</dt><dd>{details.email || '—'}</dd></div>
              <div><dt>Telefone</dt><dd>{formatPhone(details.phone)}</dd></div>
              <div><dt>Nascimento</dt><dd>{formatDate(details.birth_date)}</dd></div>
              <div className="pixl-largo"><dt>Endereço</dt><dd>{formatAddress(details)}</dd></div>
              {details._antigo ? (
                <>
                  <div><dt>Matrícula</dt><dd>{asText(details._antigo.enrollment)}</dd></div>
                  <div><dt>Paciente</dt><dd>{asText(details._antigo.patientName)}</dd></div>
                  <div><dt>Recorrência</dt><dd>{asText(details._antigo.recurrenceStatus)}</dd></div>
                  <div><dt>Cobrança</dt><dd>{asText(details._antigo.chargeStatus)}</dd></div>
                </>
              ) : (
                <>
                  <div><dt>Plano</dt><dd>{details.plan_name || '—'}{details.plan_value ? ` · ${brl(details.plan_value)}` : ''}</dd></div>
                  <div><dt>Vendedor</dt><dd>{details.titular_plano_vendedor || '—'}</dd></div>
                </>
              )}
              <div><dt>Cadastrado em</dt><dd>{formatDate(details.created_date)}</dd></div>
              <div><dt>Status</dt><dd>{details.status || '—'}</dd></div>
            </dl>
            {!details._antigo && (
              <>
                <h4 className="pixl-sub-titulo">Dependentes</h4>
                {Array.isArray(details.dependents) && details.dependents.length > 0 ? (
                  <ul className="pixl-deps">
                    {details.dependents.map((d, i) => <li key={d.id || i}>{String(d.nome || '—')} · {GRAU_LABELS[d.grau] || '—'}{d.dataNascimento ? ` · ${formatDate(d.dataNascimento)}` : ''}</li>)}
                  </ul>
                ) : <p className="pixl-nota">Nenhum dependente cadastrado.</p>}
              </>
            )}
          </div>
        </div>
      )}

      {toDelete && (
        <div className="pixl-fundo" onClick={closeDelete}>
          <div className="pixl-modal" role="dialog" aria-modal="true" aria-labelledby="pixl-apagar-titulo" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Escape') closeDelete(); }}>
            <div className="pixl-modal-topo">
              <h3 id="pixl-apagar-titulo">Apagar cliente Pix</h3>
              <button type="button" className="pixl-fechar" onClick={closeDelete} aria-label="Fechar" disabled={deleting}><X size={18} /></button>
            </div>
            <p>Apagar <strong>{nameOf(toDelete)}</strong> (CPF {formatCpf(toDelete.cpf)})?</p>
            <p className="pixl-nota">Remove o cadastro do Souli e não pode ser desfeito. Não cancela recorrência no Pix: se o cliente tiver alguma em aberto, o sistema recusa e você precisa cancelá-la primeiro.</p>
            {blocking && (
              <div className="pixl-aviso" role="alert">
                <AlertTriangle size={16} />
                <div>
                  <strong>Não apaguei: este cliente tem recorrência em aberto no Pix.</strong>
                  <ul className="pixl-deps">
                    {blocking.map((r) => <li key={r.id}>{STATUS_LABELS[String(r.status).toUpperCase()] || r.status} · {brl(r.amount)} · jornada {r.jornada} · início {formatDate(r.startDate)}</li>)}
                  </ul>
                  <p className="pixl-nota">Cancele a recorrência no Pix antes de apagar o cliente.</p>
                </div>
              </div>
            )}
            {deleteError && <p className="pixl-erro-linha" role="alert">{deleteError}</p>}
            <div className="pixl-acoes-fim">
              <button type="button" className="pixl-btn" onClick={closeDelete} disabled={deleting}>{blocking ? 'Fechar' : 'Cancelar'}</button>
              {!blocking && <button type="button" className="pixl-btn pixl-btn-perigo" onClick={confirmDelete} disabled={deleting}>{deleting ? 'Apagando...' : 'Apagar cliente'}</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
